import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/utils";
import { VegDot } from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Plus,
  Minus,
  ShoppingBag,
  Check,
  Loader2,
  Search,
  ReceiptText,
  Clock3,
  ChevronRight,
  UtensilsCrossed,
  X,
  SlidersHorizontal,
  Grid2X2,
  Soup,
  Coffee,
  Sandwich,
  Utensils,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/common/BrandLogo";

/*
|--------------------------------------------------------------------------
| QR PUBLIC MENU
|--------------------------------------------------------------------------
| Customer-facing restaurant QR ordering page.
|
| Flow:
| QR Scan
|   ↓
| Public Menu
|   ↓
| Add items
|   ↓
| Place Order
|   ↓
| Restaurant Approval
|   ↓
| KOT / Kitchen
|   ↓
| Preparing / Ready / Served
|   ↓
| Request Bill
|   ↓
| Restaurant handles payment
|
| Payment gateway is intentionally NOT integrated here.
|--------------------------------------------------------------------------
*/

const QRHero = ({ table, restaurant }) => (
  <section className="relative overflow-hidden">
    <div className="relative h-[270px] sm:h-[320px]">
      <img
        src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1800&q=90"
        alt="Restaurant food"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/15" />

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20" />

      <div className="relative z-10 mx-auto h-full max-w-6xl px-4 sm:px-7">
        <div className="flex items-start justify-between gap-3 pt-4 sm:pt-5">
          <div className="flex items-center">
            <div>
              <p className="text-base font-black tracking-tight text-white">
                {restaurant?.name || "Restaurant"}
              </p>

              {restaurant?.tagline && (
                <p className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.2em] text-orange-300">
                  {restaurant.tagline}
                </p>
              )}
            </div>
          </div>

          {table && (
            <div className="flex items-center gap-1.5 rounded-full border border-orange-300/50 bg-black/35 px-3 py-1.5 text-[10px] font-bold text-white backdrop-blur-md sm:px-3.5 sm:py-2 sm:text-xs">
              <Utensils size={13} className="text-orange-300" />
              Table {table}
            </div>
          )}
        </div>

        <div className="absolute bottom-9 left-4 max-w-lg sm:left-7 sm:bottom-11">
          <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-orange-300/30 bg-black/25 px-2.5 py-1.5 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]" />

            <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/90 sm:text-[9px]">
              Dine-in ordering
            </span>
          </div>

          <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.25em] text-orange-300 sm:text-[10px]">
            Welcome
          </p>

          <h1 className="text-[31px] font-black leading-[0.98] tracking-tight text-white sm:text-5xl">
            Good Food,
            <br />
            <span className="text-orange-400">Great Moments</span>
          </h1>

          <p className="mt-2 max-w-sm text-[11px] leading-relaxed text-white/70 sm:text-sm">
            Browse our menu and order directly from your table.
          </p>
        </div>
      </div>
    </div>

    <div className="absolute -bottom-3 left-0 right-0 h-8 rounded-[50%] bg-[#faf8f5]" />
  </section>
);

export default function QRMenu() {
  const [params] = useSearchParams();

  const branchId = params.get("branch_id");
  const table = params.get("table") || "";
  const qrToken = params.get("t") || "";

  const [data, setData] = useState(null);

  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");

  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);

  const [placed, setPlaced] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [track, setTrack] = useState(null);
  const [orderingMore, setOrderingMore] = useState(false);

  const QR_ORDER_STORAGE_KEY = "stayserve_qr_active_order";

  const [busy, setBusy] = useState(false);
  const [billBusy, setBillBusy] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [tableId, setTableId] = useState(null);
  const [sessionFinished, setSessionFinished] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Load public menu
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!branchId) return;

    let mounted = true;

    const loadMenu = async () => {
      try {
        const r = await api.publicMenu(branchId, table, qrToken);
        setData(r.data);
        setTableId(r.data.table_id || null);

        if (mounted) {
          setData(r.data);
        }
      } catch (error) {
        console.error("Public menu error:", error);

        if (mounted) {
          setData({ error: true });
        }
      }
    };

    loadMenu();

    return () => {
      mounted = false;
    };
  }, [branchId, table, qrToken]);

  useEffect(() => {
    if (sessionFinished) return;

    try {
      const saved = localStorage.getItem(QR_ORDER_STORAGE_KEY);

      if (!saved) return;

      // Wait until public menu API has resolved the actual table ID.
      // On initial render tableId is null.
      if (!branchId || !tableId || !qrToken) return;

      const parsed = JSON.parse(saved);

      // Make sure saved order belongs to this exact QR table/session
      if (
        parsed.branch_id !== branchId ||
        parsed.table_id !== tableId ||
        parsed.qr_token !== qrToken ||
        !parsed.order_id
      ) {
        localStorage.removeItem(QR_ORDER_STORAGE_KEY);
        return;
      }

      setOrderId(parsed.order_id);
      setPlaced(parsed.order_no || null);
    } catch (error) {
      console.error("Failed to restore QR order:", error);
      localStorage.removeItem(QR_ORDER_STORAGE_KEY);
    }
  }, [branchId, tableId, qrToken, sessionFinished]);
  /*
  |--------------------------------------------------------------------------
  | Track current order
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (sessionFinished || !orderId || !branchId || !tableId || !qrToken) {
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        const r = await api.publicOrderStatus(
          orderId,
          branchId,
          tableId,
          qrToken,
        );

        if (mounted) {
          const latestOrder = r.data;

          const paymentCompleted =
            latestOrder?.payment_status === "paid" &&
            latestOrder?.status === "completed";

          if (paymentCompleted) {
            // ---------------------------------------------------------
            // OLD ORDER IS COMPLETED
            // Clear only the OLD order.
            //
            // IMPORTANT:
            // The QR/table session itself is NOT finished.
            // Same QR must be allowed to create a NEW order.
            // ---------------------------------------------------------

            localStorage.removeItem(QR_ORDER_STORAGE_KEY);

            setTrack(null);
            setOrderId(null);
            setPlaced(null);
            setOrderingMore(false);
            setCart([]);
            setOpen(false);

            // IMPORTANT:
            // Do NOT set sessionFinished(true).
            setSessionFinished(false);

            return;
          }

          // ---------------------------------------------------------
          // REJECTED ORDER
          //
          // Rejected order is finished for this QR ordering session.
          // Clear only the OLD order state so customer can place
          // a completely NEW order using the same QR.
          // ---------------------------------------------------------

          const orderRejected = latestOrder?.approval_status === "rejected";

          if (orderRejected) {
            // Keep the rejected order in state so the customer
            // can clearly see why the restaurant rejected it.
            setTrack(latestOrder);

            return;
          }

          setTrack(latestOrder);
        }
      } catch (error) {
        console.error("Order tracking error:", error);

        if (error?.response?.status === 404) {
          localStorage.removeItem(QR_ORDER_STORAGE_KEY);

          setTrack(null);
          setOrderId(null);
          setPlaced(null);
          setOrderingMore(false);
          setCart([]);
          setOpen(false);
          setSessionFinished(false);
        }
      }
    };

    load();

    const timer = setInterval(load, 4000);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [orderId, branchId, tableId, qrToken, sessionFinished]);

  /*
  |--------------------------------------------------------------------------
  | Menu filtering
  |--------------------------------------------------------------------------
  */

  const items = useMemo(() => {
    if (!data?.items) return [];

    const query = search.trim().toLowerCase();

    return data.items.filter((item) => {
      const categoryMatch =
        activeCat === "all" || item.category_id === activeCat;

      const searchMatch =
        !query ||
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query);

      return categoryMatch && searchMatch;
    });
  }, [data, activeCat, search]);

  /*
  |--------------------------------------------------------------------------
  | Cart
  |--------------------------------------------------------------------------
  */

  const add = (item) => {
    setCart((current) => {
      const existing = current.find((x) => x.menu_item_id === item.id);

      if (existing) {
        return current.map((x) =>
          x.menu_item_id === item.id
            ? {
                ...x,
                qty: x.qty + 1,
              }
            : x,
        );
      }

      return [
        ...current,
        {
          menu_item_id: item.id,
          name: item.name,
          price: Number(item.price || 0),
          qty: 1,
          veg: item.veg,
          station: item.station || "Main Kitchen",
          modifiers: [],
          notes: "",
        },
      ];
    });
  };

  const change = (id, delta) => {
    setCart((current) =>
      current
        .map((item) =>
          item.menu_item_id === id
            ? {
                ...item,
                qty: Math.max(0, item.qty + delta),
              }
            : item,
        )
        .filter((item) => item.qty > 0),
    );
  };

  const removeFromCart = (id) => {
    setCart((current) => {
      const updatedCart = current.filter((item) => item.menu_item_id !== id);

      // Close order sheet when the last item is removed
      if (updatedCart.length === 0) {
        setOpen(false);
      }

      return updatedCart;
    });
  };

  const count = cart.reduce((sum, item) => sum + item.qty, 0);

  const total = cart.reduce(
    (sum, item) => sum + Number(item.price || 0) * item.qty,
    0,
  );

  /*
  |--------------------------------------------------------------------------
  | Place order
  |--------------------------------------------------------------------------
  */
  const place = async () => {
    if (!cart.length) {
      toast.error("Please add at least one item");
      return;
    }

    if (!tableId) {
      toast.error("Table information is missing");
      return;
    }

    if (!qrToken) {
      toast.error("Invalid QR session");
      return;
    }

    setBusy(true);

    try {
      const r = await api.publicOrder({
        branch_id: branchId,

        source: "qr",

        type: "dine_in",

        // Actual table ID returned by public menu API
        table_id: tableId,

        qr_token: qrToken || null,

        table_name: table || null,

        customer_name: name.trim() || null,

        customer_phone: phone.trim() || null,

        items: cart,

        note: table ? `QR Order · ${table}` : "QR Order",
      });

      const result = r.data;

      const activeOrderId = result.order_id || result.id;
      const activeOrderNo = result.order_no;

      setPlaced(activeOrderNo);
      setOrderId(activeOrderId);

      localStorage.setItem(
        QR_ORDER_STORAGE_KEY,
        JSON.stringify({
          order_id: activeOrderId,
          order_no: activeOrderNo,
          branch_id: branchId,
          table_id: tableId,
          qr_token: qrToken,
        }),
      );

      setCart([]);
      setOpen(false);

      toast.success("Order placed successfully");
    } catch (error) {
      console.error("Place order error:", error);
      console.error("Backend response:", error?.response?.data);

      const detail = error?.response?.data?.detail;

      let message = "Could not place order";

      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((item) => item?.msg || "Invalid request")
          .join(", ");
      } else if (detail && typeof detail === "object") {
        message = detail?.msg || "Invalid request";
      } else if (error?.message) {
        message = error.message;
      }

      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Request bill
  |--------------------------------------------------------------------------
  */

  const requestBill = async () => {
    if (sessionFinished || !orderId || billBusy) return;

    setBillBusy(true);

    try {
      await api.publicRequestBill(orderId, branchId, tableId, qrToken);

      toast.success("Bill requested — restaurant staff notified");

      try {
        const r = await api.publicOrderStatus(
          orderId,
          branchId,
          tableId,
          qrToken,
        );
        setTrack(r.data);
      } catch (_) {}
    } catch (error) {
      console.error("Request bill error:", error);

      toast.error(error?.response?.data?.detail || "Failed to request bill");
    } finally {
      setBillBusy(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Order more
  |--------------------------------------------------------------------------
  */

  const startOrderMore = () => {
    // ---------------------------------------------------------
    // No old active order
    //
    // This normally happens after payment/completion.
    // A new order should be created from the main menu instead.
    // ---------------------------------------------------------
    if (!orderId || !track) {
      toast.error("Please place a new order first.");
      return;
    }

    // ---------------------------------------------------------
    // Completed / paid order can never receive additions
    // ---------------------------------------------------------
    if (track?.status === "completed" || track?.payment_status === "paid") {
      toast.error("This order has been completed. Please place a new order.");
      return;
    }

    // ---------------------------------------------------------
    // Once bill is requested/generated, additions are blocked
    // until this order is completed.
    // ---------------------------------------------------------
    if (
      track?.bill_requested ||
      track?.bill_status === "generated" ||
      track?.bill_status === "paid" ||
      track?.bill_status === "closed"
    ) {
      toast.error(
        "Bill has already been requested. You cannot add more items.",
      );
      return;
    }

    setSearch("");
    setActiveCat("all");
    setCart([]);
    setOrderingMore(true);
    setOpen(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const addMoreItems = async () => {
    if (!orderId || !track) {
      toast.error("Active order not found");
      return;
    }

    if (track?.status === "completed" || track?.payment_status === "paid") {
      toast.error(
        "This order has already been completed. Please place a new order.",
      );
      return;
    }

    if (
      track?.bill_requested ||
      track?.bill_status === "generated" ||
      track?.bill_status === "paid" ||
      track?.bill_status === "closed"
    ) {
      toast.error(
        "Bill has already been requested. You cannot add more items.",
      );
      return;
    }

    if (!tableId) {
      toast.error("Table information is missing");
      return;
    }

    if (!qrToken) {
      toast.error("Invalid QR session");
      return;
    }

    if (!cart.length) {
      toast.error("Please add at least one item");
      return;
    }

    setBusy(true);

    try {
      const response = await api.publicAddOrderItems(orderId, {
        branch_id: branchId,
        table_id: tableId,
        qr_token: qrToken,
        items: cart,
        note: "",
      });

      setCart([]);

      const statusResponse = await api.publicOrderStatus(
        orderId,
        branchId,
        tableId,
        qrToken,
      );

      setTrack(statusResponse.data);
      setOrderingMore(false);
      setOpen(false);

      toast.success("Additional items sent for approval");
    } catch (error) {
      console.error("Add more items error:", error);

      toast.error(error?.response?.data?.detail || "Unable to add items");
    } finally {
      setBusy(false);
    }
  };

  const startNewOrder = () => {
    // Clear the rejected/old order
    localStorage.removeItem(QR_ORDER_STORAGE_KEY);

    // Reset order state
    setTrack(null);
    setOrderId(null);
    setPlaced(null);

    // Reset menu/cart state
    setOrderingMore(false);
    setCart([]);
    setOpen(false);
    setSearch("");
    setActiveCat("all");

    // Keep the QR session active
    setSessionFinished(false);

    // Return to top of the original menu
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  /*
  |--------------------------------------------------------------------------
  | Invalid QR
  |--------------------------------------------------------------------------
  */

  if (!branchId) {
    return (
      <Center>
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-orange-50 text-primary">
            <UtensilsCrossed size={25} />
          </div>

          <h2 className="text-lg font-black text-stone-900">Invalid QR link</h2>

          <p className="mt-2 text-sm text-stone-500">
            Please scan the restaurant table QR again.
          </p>
        </div>
      </Center>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (!data) {
    return (
      <Center>
        <div className="text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-primary">
            <Loader2 className="animate-spin" size={23} />
          </div>

          <p className="font-bold text-stone-900">Preparing your menu</p>

          <p className="mt-1 text-xs text-stone-500">Please wait a moment…</p>
        </div>
      </Center>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Error
  |--------------------------------------------------------------------------
  */

  if (data.error) {
    return (
      <Center>
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600">
            <UtensilsCrossed size={25} />
          </div>

          <h2 className="text-lg font-black text-stone-900">
            Menu unavailable
          </h2>

          <p className="mt-2 text-sm text-stone-500">
            Please ask restaurant staff for assistance.
          </p>
        </div>
      </Center>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ORDER TRACKING
  |--------------------------------------------------------------------------
  */

  if (placed && !orderingMore) {
    const currentStatus = String(track?.status || "").toLowerCase();

    const isPreparing = ["preparing", "in_progress"].includes(currentStatus);
    const isReady = ["ready", "ready_to_serve"].includes(currentStatus);
    const isServed = ["served", "completed"].includes(currentStatus);

    const steps = [
      {
        k: "placed",
        label: "Order received",
        description: "Your order has been sent to the restaurant.",
        done: true,
      },
      {
        k: "approved",
        label: "Approved by staff",
        description: "Restaurant is processing your order.",
        done: track?.approval_status === "approved",
      },
      {
        k: "kitchen",
        label: "Preparing",
        description: "Your food is being prepared.",
        done: isPreparing || isReady || isServed,
      },
      {
        k: "ready",
        label: "Ready to Serve",
        description: "Your order is ready to be served.",
        done: isReady || isServed,
      },
      {
        k: "served",
        label: "Served",
        description: "Your order has been served at the table.",
        done: isServed,
      },
    ];
    const rejected = track?.approval_status === "rejected";

    const billReady = track?.bill_status === "generated";

    const billRequested = track?.bill_requested === true;

    const pendingAdditions = Array.isArray(track?.pending_additions)
      ? track.pending_additions
      : [];

    const hasPendingAddition = pendingAdditions.length > 0;

    const latestAddition = track?.latest_addition || null;

    return (
      <div className="min-h-screen bg-[#faf8f5] text-stone-900">
        {/* Tracking header */}

        <QRHero table={table} restaurant={data?.restaurant} />

        <main className="mx-auto flex min-h-[calc(100vh-180px)] w-full max-w-2xl flex-col px-4 pb-8 sm:px-6">
          <div className="-mt-4 relative z-10">
            <div className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white px-4 py-3 shadow-lg shadow-black/[0.05]">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-stone-400">
                  Current total
                </p>

                <p className="mt-0.5 text-xl font-black text-primary">
                  {money(track?.total || 0)}
                </p>
              </div>

              <div className="grid h-10 w-10 place-items-center rounded-xl bg-orange-50 text-primary">
                <ReceiptText size={19} />
              </div>
            </div>
          </div>

          {/* Status */}

          <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            {rejected ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <div className="flex gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-red-100 text-red-600">
                    <X size={18} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-red-800">
                      Order could not be accepted
                    </p>

                    {track?.rejection_reason && (
                      <p className="mt-1 text-xs leading-relaxed text-red-600">
                        {track.rejection_reason}
                      </p>
                    )}

                    <p className="mt-2 text-[11px] text-red-500">
                      Please contact restaurant staff.
                    </p>

                    <Button
                      onClick={startNewOrder}
                      className="mt-4 h-10 rounded-xl px-4 text-xs font-bold shadow-sm"
                      data-testid="qr-start-new-order"
                    >
                      <Plus size={15} className="mr-1.5" />
                      Start New Order
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400">
                    Order status
                  </p>

                  <h2 className="mt-1 text-lg font-black">
                    We are taking care of it
                  </h2>
                </div>

                <div className="space-y-5">
                  {steps.map((step, index) => (
                    <div key={step.k} className="relative flex gap-3">
                      {index !== steps.length - 1 && (
                        <div
                          className={cn(
                            "absolute left-[14px] top-7 h-8 w-0.5",
                            step.done ? "bg-emerald-300" : "bg-stone-200",
                          )}
                        />
                      )}

                      <div
                        className={cn(
                          "relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full",
                          step.done
                            ? "bg-emerald-500 text-white"
                            : "bg-stone-100 text-stone-400",
                        )}
                      >
                        {step.done ? (
                          <Check size={14} />
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        )}
                      </div>

                      <div className="pt-0.5">
                        <p
                          className={cn(
                            "text-sm font-bold",
                            step.done ? "text-stone-900" : "text-stone-400",
                          )}
                        >
                          {step.label}
                        </p>

                        <p className="mt-0.5 text-[11px] leading-relaxed text-stone-500">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {hasPendingAddition && (
            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
                  <Clock3 size={17} />
                </div>

                <div>
                  <p className="text-sm font-bold text-amber-900">
                    Additional items waiting for approval
                  </p>

                  <p className="mt-1 text-xs leading-relaxed text-amber-700">
                    Your additional items have been sent to the restaurant. They
                    will be sent to the kitchen after approval.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Bill */}

          {!rejected && (
            <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-50 text-primary">
                  <ReceiptText size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold">Your Bill</p>

                  <p className="text-[11px] text-stone-500">
                    Payment is handled at the restaurant.
                  </p>
                </div>
              </div>

              {billReady ? (
                <div>
                  <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-stone-500">
                        Total amount
                      </span>

                      <span className="text-lg font-black">
                        {money(track?.total || 0)}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2 border-t border-stone-200 pt-3 text-emerald-700">
                      <div className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100">
                        <Check size={13} />
                      </div>

                      <span className="text-xs font-bold">Bill generated</span>
                    </div>
                  </div>

                  <div className="mt-3 rounded-xl border border-orange-100 bg-orange-50 p-3.5">
                    <p className="text-xs font-bold text-orange-900">
                      Please pay at the restaurant counter.
                    </p>

                    <p className="mt-1 text-[11px] leading-relaxed text-orange-700">
                      Cash, UPI, card or any payment method accepted by the
                      restaurant.
                    </p>
                  </div>
                </div>
              ) : billRequested ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
                      <Clock3 size={17} />
                    </div>

                    <div>
                      <p className="text-sm font-bold text-amber-900">
                        Bill requested
                      </p>

                      <p className="mt-1 text-xs leading-relaxed text-amber-700">
                        Restaurant staff has been notified. Your final bill will
                        appear here once it is generated.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={requestBill}
                  disabled={billBusy || track?.approval_status !== "approved"}
                  className="h-11 w-full rounded-xl text-sm font-bold shadow-md shadow-orange-500/15"
                  data-testid="qr-request-bill"
                >
                  {billBusy ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={17} />
                      Requesting…
                    </>
                  ) : (
                    <>
                      <ReceiptText size={17} className="mr-2" />
                      Request Bill
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Order More */}

          {!rejected && !billRequested && !billReady && (
            <div className="mt-3">
              <Button
                variant="outline"
                onClick={startOrderMore}
                className="h-11 w-full rounded-xl border-stone-200 bg-white text-sm font-bold hover:bg-stone-50"
                data-testid="qr-order-more"
              >
                <Plus size={17} className="mr-2" />
                Order More Items
              </Button>

              <p className="mt-1.5 text-center text-[10px] text-stone-400">
                Add more items to your current table order.
              </p>
            </div>
          )}

          <PremiumFooter />
        </main>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | MAIN MENU
  |--------------------------------------------------------------------------
  */

  return (
    <div className="flex min-h-screen flex-col bg-[#faf8f5] text-stone-900">
      {/* =========================================================
          COMPACT PREMIUM HERO
      ========================================================= */}

      <QRHero table={table} restaurant={data?.restaurant} />

      {/* =========================================================
          SEARCH + FILTER
      ========================================================= */}

      <section className="relative z-20 -mt-1">
        <div className="mx-auto max-w-6xl px-4 sm:px-7">
          <div className="flex items-center gap-2.5">
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"
              />

              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search dishes..."
                className="h-12 rounded-2xl border-stone-200 bg-white pl-11 pr-10 text-sm shadow-md shadow-black/[0.04] focus-visible:ring-primary/20 sm:h-13"
                data-testid="qr-menu-search"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <button
              type="button"
              className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-stone-200 bg-white text-stone-700 shadow-md shadow-black/[0.04] sm:h-13 sm:w-13"
              aria-label="Menu filters"
            >
              <SlidersHorizontal size={18} />
            </button>
          </div>

          {/* Categories */}

          <div className="mt-3 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 pb-1">
              <CategoryChip
                active={activeCat === "all"}
                icon={<Grid2X2 size={15} />}
                onClick={() => setActiveCat("all")}
              >
                All
              </CategoryChip>

              {(data.categories || []).map((category, index) => (
                <CategoryChip
                  key={category.id}
                  active={activeCat === category.id}
                  icon={<CategoryIcon index={index} />}
                  onClick={() => setActiveCat(category.id)}
                >
                  {category.name}
                </CategoryChip>
              ))}
            </div>
          </div>
        </div>
      </section>

      {orderingMore && track && (
        <div className="mx-auto mb-4 w-full max-w-6xl px-4 sm:px-7">
          <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400">
                  Current Bill
                </p>

                <p className="mt-1 text-2xl font-black text-primary">
                  {money(track?.total || 0)}
                </p>

                <p className="mt-1 text-[10px] leading-relaxed text-stone-500">
                  Your existing order total. New items will be added after
                  restaurant approval.
                </p>
              </div>

              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-orange-50 text-primary">
                <ReceiptText size={20} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          MAIN CONTENT
      ========================================================= */}

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 pb-8 pt-6 sm:px-7 sm:pt-7">
        {/* Menu heading */}

        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-stone-400">
              {orderingMore ? "Add More Items" : "Our Menu"}
            </p>

            <h2 className="mt-0.5 text-xl font-black tracking-tight sm:text-2xl">
              {orderingMore
                ? "Add more items to your order"
                : "Freshly prepared for you"}
            </h2>

            {orderingMore && (
              <p className="mt-1 text-xs text-stone-500">
                Your new items will be added to the current table order.
              </p>
            )}
          </div>

          <span className="shrink-0 text-xs font-medium text-stone-400">
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
        </div>

        {/* Menu */}

        {items.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-stone-100">
              <Search size={21} className="text-stone-400" />
            </div>

            <p className="font-bold">No dishes found</p>

            <p className="mt-1 text-xs text-stone-500">
              Try another search or category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {items.map((item) => {
              const inCart = cart.find((x) => x.menu_item_id === item.id);

              return (
                <MenuCard
                  key={item.id}
                  item={item}
                  inCart={inCart}
                  onAdd={add}
                  onChange={change}
                />
              );
            })}
          </div>
        )}

        {/* =======================================================
            FEATURES
        ======================================================= */}

        <div className="mt-6 rounded-2xl border border-stone-200 bg-white px-3 py-3 shadow-sm sm:px-5">
          <div className="grid grid-cols-3 gap-2 sm:gap-5">
            <Feature
              icon="shield"
              title="Safe & Hygienic"
              description="Your safety is our priority"
            />

            <Feature
              icon="leaf"
              title="Fresh Ingredients"
              description="Made with quality ingredients"
            />

            <Feature
              icon="heart"
              title="Made with Love"
              description="By our expert chefs"
            />
          </div>
        </div>

        {/* Footer */}

        <PremiumFooter />
      </main>

      {/* =========================================================
          FLOATING CART
      ========================================================= */}

      {count > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="qr-cart-btn"
          className="fixed bottom-3 left-3 right-3 z-40 mx-auto flex h-14 max-w-xl items-center justify-between rounded-2xl border border-orange-400/20 bg-gradient-to-r from-orange-600 to-orange-500 px-3.5 text-white shadow-[0_12px_35px_rgba(234,88,12,0.28)] sm:bottom-4 sm:h-16 sm:px-5"
        >
          <span className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/15 sm:h-10 sm:w-10">
              <ShoppingBag size={18} />
            </span>

            <span className="text-left">
              <span className="block text-xs font-black sm:text-sm">
                {count} item
                {count > 1 ? "s" : ""}
              </span>

              <span className="block text-[9px] text-white/70">View Cart</span>
            </span>
          </span>

          <span className="flex items-center gap-2">
            <span className="text-base font-black sm:text-lg">
              {money(total)}
            </span>

            <span className="grid h-8 w-8 place-items-center rounded-full bg-white text-orange-600 sm:h-9 sm:w-9">
              <ChevronRight size={18} />
            </span>
          </span>
        </button>
      )}

      {/* =========================================================
          CART SHEET
      ========================================================= */}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[92vh] overflow-y-auto rounded-t-[28px] bg-[#faf8f5] px-4 sm:px-6"
        >
          <SheetHeader className="mx-auto max-w-2xl pt-1">
            <SheetTitle className="text-xl font-black">Your Order</SheetTitle>
          </SheetHeader>

          <div className="mx-auto max-w-2xl">
            {/* Cart items */}

            <div className="mt-4 space-y-2.5">
              {cart.map((item) => (
                <div
                  key={item.menu_item_id}
                  className="rounded-2xl border border-stone-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-center gap-2.5">
                    <VegDot veg={item.veg} className="h-3.5 w-3.5 shrink-0" />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold">{item.name}</p>

                      <p className="mt-0.5 text-[10px] text-stone-500">
                        {money(item.price)} each
                      </p>
                    </div>

                    <div className="flex items-center gap-0.5 rounded-xl border border-stone-200 bg-stone-50 px-0.5 py-0.5">
                      <button
                        type="button"
                        onClick={() => change(item.menu_item_id, -1)}
                        className="grid h-7 w-7 place-items-center rounded-lg hover:bg-white"
                      >
                        <Minus size={13} />
                      </button>

                      <span className="w-6 text-center text-xs font-black">
                        {item.qty}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          add({
                            id: item.menu_item_id,
                            ...item,
                          })
                        }
                        className="grid h-7 w-7 place-items-center rounded-lg hover:bg-white"
                      >
                        <Plus size={13} />
                      </button>
                    </div>

                    <div className="w-16 text-right">
                      <p className="text-xs font-black">
                        {money(item.price * item.qty)}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFromCart(item.menu_item_id)}
                    className="ml-6 mt-2 text-[10px] font-medium text-red-500"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {/* Customer details */}

            <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-4">
              <div className="mb-3">
                <p className="text-sm font-bold">
                  Customer details
                  <span className="ml-1 text-[10px] font-normal text-stone-400">
                    optional
                  </span>
                </p>

                <p className="mt-0.5 text-[10px] text-stone-400">
                  Helpful if the restaurant needs to contact you.
                </p>
              </div>

              <div className="space-y-2.5">
                <Input
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 rounded-xl border-stone-200 bg-stone-50 text-sm"
                  data-testid="qr-name"
                />

                <Input
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-10 rounded-xl border-stone-200 bg-stone-50 text-sm"
                  inputMode="tel"
                  data-testid="qr-phone"
                />
              </div>
            </div>

            {/* Total */}

            <div className="mt-3 mb-4 rounded-2xl border border-stone-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-stone-400">
                    Total
                  </p>

                  <p className="mt-0.5 text-xl font-black text-primary">
                    {money(total)}
                  </p>
                </div>

                <ShoppingBag size={22} className="text-primary" />
              </div>

              <Button
                onClick={orderingMore ? addMoreItems : place}
                disabled={busy || cart.length === 0}
                className="mt-4 h-11 w-full rounded-xl text-sm font-bold shadow-md shadow-orange-500/15"
                data-testid="qr-place-order"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={17} />
                    {orderingMore ? "Adding Items…" : "Placing Order…"}
                  </>
                ) : (
                  <>
                    <ShoppingBag size={17} className="mr-2" />
                    {orderingMore ? "Add to Order" : "Place Order"}
                  </>
                )}
              </Button>

              <p className="mt-2 text-center text-[10px] text-stone-400">
                Your order will be sent to the restaurant for approval.
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| MENU CARD
|--------------------------------------------------------------------------
*/

const MenuCard = ({ item, inCart, onAdd, onChange }) => {
  return (
    <div
      className="group rounded-2xl border border-stone-200 bg-white p-2.5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md sm:p-3"
      data-testid={`qr-item-${item.id}`}
    >
      <div className="flex gap-3">
        {/* Image */}

        <div className="relative shrink-0">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="h-[92px] w-[92px] rounded-xl object-cover transition-transform duration-200 group-hover:scale-[1.02] sm:h-[108px] sm:w-[108px]"
            />
          ) : (
            <div className="grid h-[92px] w-[92px] place-items-center rounded-xl bg-stone-100 sm:h-[108px] sm:w-[108px]">
              <UtensilsCrossed size={23} className="text-stone-400" />
            </div>
          )}

          {item.veg !== undefined && (
            <div className="absolute left-1.5 top-1.5 rounded-md bg-white/95 p-1 shadow-sm backdrop-blur-sm">
              <VegDot veg={item.veg} className="h-3.5 w-3.5" />
            </div>
          )}
        </div>

        {/* Content */}

        <div className="flex min-w-0 flex-1 flex-col py-0.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <VegDot veg={item.veg} className="h-3 w-3 shrink-0" />

              <p className="truncate text-sm font-black leading-tight sm:text-base">
                {item.name}
              </p>
            </div>

            {item.description && (
              <p className="mt-1 line-clamp-2 text-[10px] leading-relaxed text-stone-500 sm:text-xs">
                {item.description}
              </p>
            )}
          </div>

          <div className="mt-auto flex items-end justify-between gap-2 pt-2">
            <p className="text-base font-black text-primary sm:text-lg">
              {money(item.price)}
            </p>

            {inCart ? (
              <div className="flex items-center gap-0.5 rounded-xl bg-orange-600 px-1 py-1 text-white shadow-sm">
                <button
                  type="button"
                  onClick={() => onChange(item.id, -1)}
                  className="grid h-7 w-7 place-items-center rounded-lg hover:bg-white/10"
                  data-testid={`qr-minus-${item.id}`}
                >
                  <Minus size={14} />
                </button>

                <span className="w-6 text-center text-xs font-black">
                  {inCart.qty}
                </span>

                <button
                  type="button"
                  onClick={() => onAdd(item)}
                  className="grid h-7 w-7 place-items-center rounded-lg hover:bg-white/10"
                  data-testid={`qr-plus-${item.id}`}
                >
                  <Plus size={14} />
                </button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => onAdd(item)}
                className="h-8 rounded-lg px-3 text-xs font-bold shadow-sm"
                data-testid={`qr-add-${item.id}`}
              >
                <Plus size={14} className="mr-1" />
                Add
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| CATEGORY CHIP
|--------------------------------------------------------------------------
*/

const CategoryChip = ({ active, children, icon, ...props }) => (
  <button
    type="button"
    {...props}
    className={cn(
      "flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-xs font-bold transition-all duration-200",
      active
        ? "bg-orange-600 text-white shadow-md shadow-orange-500/15"
        : "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50",
    )}
  >
    {icon}
    {children}
  </button>
);

/*
|--------------------------------------------------------------------------
| CATEGORY ICON
|--------------------------------------------------------------------------
*/

const CategoryIcon = ({ index }) => {
  const icons = [
    <Soup key="soup" size={15} />,
    <Utensils key="utensils" size={15} />,
    <Sandwich key="sandwich" size={15} />,
    <Coffee key="coffee" size={15} />,
  ];

  return icons[index % icons.length];
};

/*
|--------------------------------------------------------------------------
| FEATURE
|--------------------------------------------------------------------------
*/

const Feature = ({ icon, title, description }) => {
  const icons = {
    shield: (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 sm:h-5 sm:w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      >
        <path d="M12 3l7 3v5c0 4.8-3 8.5-7 10-4-1.5-7-5.2-7-10V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),

    leaf: (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 sm:h-5 sm:w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      >
        <path d="M20 4C11 4 5 8 5 14c0 3 2 5 5 5 6 0 10-6 10-15z" />
        <path d="M4 21c2-5 6-8 12-11" />
      </svg>
    ),

    heart: (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 sm:h-5 sm:w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      >
        <path d="M20.8 8.7c0 5.5-8.8 10.4-8.8 10.4S3.2 14.2 3.2 8.7A4.7 4.7 0 0 1 12 6.4a4.7 4.7 0 0 1 8.8 2.3z" />
      </svg>
    ),
  };

  return (
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-orange-50 text-stone-700 sm:h-9 sm:w-9 sm:rounded-xl">
        {icons[icon]}
      </div>

      <div className="min-w-0">
        <p className="truncate text-[9px] font-bold leading-tight sm:text-xs">
          {title}
        </p>

        <p className="hidden truncate text-[10px] leading-tight text-stone-500 sm:block">
          {description}
        </p>
      </div>
    </div>
  );
};

/*
|--------------------------------------------------------------------------
| PREMIUM FOOTER
|--------------------------------------------------------------------------
*/

const PremiumFooter = () => (
  <footer className="mt-auto shrink-0 pt-8 pb-2 text-center">
    <div className="mx-auto mb-3 flex max-w-[180px] items-center justify-center gap-2">
      <div className="h-px flex-1 bg-stone-200" />

      <span className="text-[10px] text-stone-300">✦</span>

      <div className="h-px flex-1 bg-stone-200" />
    </div>

    <p className="text-xs text-stone-500">
      Thank you for dining with us!
      <span className="ml-1">❤️</span>
    </p>

    <p className="mt-1.5 text-[10px] text-stone-400">
      Powered by{" "}
      <span className="font-black tracking-wide text-orange-600">
        SSQUARE LOGIC
      </span>
    </p>
  </footer>
);

/*
|--------------------------------------------------------------------------
| CENTER STATE
|--------------------------------------------------------------------------
*/

const Center = ({ children }) => (
  <div className="grid min-h-screen place-items-center bg-[#faf8f5] p-5">
    {children}
  </div>
);
